import {Injectable, Injector} from "@angular/core";
import {ReportError} from "app/app.service";
import {BaseListService, BatchResponse} from "app/services/domain/base.service";
import * as Models from "app/services/proxy/model/models";
import {Transforms} from "app/shared/utils/transforms";
import {Lookup} from "framework/services/utils.service";
import {ControlOption} from "framework/ui/control-option";
import {MomentHelper} from "framework/ui/formatting/date-format.pipe";

@Injectable()
export class ScheduleService extends BaseListService<Models.ScheduleListRequest, Models.BatchRequest, Models.ScheduleDetails>
{
    constructor(inj: Injector)
    {
        super(inj, Models.ScheduleDetails);
    }

    @ReportError
    public list(listReq: Models.ScheduleListRequest): Promise<Models.ListResponse>
    {
        return this.app.domain.apis.schedule.scheduleList(listReq);
    }

    @ReportError
    public getSchedule(scheduleId: string): Promise<Models.ScheduleDetails>
    {
        return this.app.domain.apis.schedule.scheduleGet(scheduleId);
    }

    @ReportError
    protected async getBatchResponseImpl(req: Models.BatchRequest): Promise<BatchResponse<Models.ScheduleDetails>>
    {
        if (!req?.ids) return null;

        let batchResult: Lookup<Models.ScheduleDetails> = {};
        if (req.ids.length)
        {
            const request  = Models.RecordIdentityBatchRequest.newInstance({ids: Transforms.mapToRecordIdentities(req.ids)});
            const response = await this.app.domain.apis.schedule.schedulesBatch(request);
            for (const schedule of response || []) batchResult[schedule.schedule_id] = schedule;
        }

        return {results: batchResult};
    }


    protected createBatchRequest(listReq: Models.ScheduleListRequest,
                                 ids: string[]): Models.BatchRequest
    {
        return Models.BatchRequest.newInstance({ids: ids});
    }


    public formScheduleObject(model: ScheduleFormData)
    {
        let schedule = Models.SchedulingDetails.newInstance({
                                                                year : "*",
                                                                month: "*",
                                                                time : Models.SchedulingTimeDetails.newInstance({
                                                                                                                    hour   : `${model.start.getHours()}`,
                                                                                                                    minute : `${model.start.getMinutes()}`,
                                                                                                                    seconds: `${model.start.getSeconds()}`
                                                                                                                })

                                                            });
        switch (model.recurrence)
        {
            case Models.ScheduleRecurrence.Once:
                schedule.year       = `${model.start.getFullYear()}`;
                schedule.month      = `${(model.start.getMonth() + 1)}`;
                schedule.dayofmonth = Models.SchedulingDayDetails.newInstance({
                                                                                  day: `${model.start.getDate()}`
                                                                              });
                break;

            case Models.ScheduleRecurrence.Daily:
                schedule.dayofmonth = Models.SchedulingDayDetails.newInstance({
                                                                                  day: "*"
                                                                              });
                break;

            case Models.ScheduleRecurrence.Minutes:
                schedule.time       = Models.SchedulingTimeDetails.newInstance({
                                                                                   hour   : "*",
                                                                                   minute : `0/${this.repeatEveryValue(model)}`,
                                                                                   seconds: "0"
                                                                               });
                schedule.dayofmonth = Models.SchedulingDayDetails.newInstance({
                                                                                  day: "1/1"
                                                                              });
                break;

            case Models.ScheduleRecurrence.Hours:
                schedule.time       = Models.SchedulingTimeDetails.newInstance({
                                                                                   hour   : `0/${this.repeatEveryValue(model)}`,
                                                                                   minute : "0",
                                                                                   seconds: "0"
                                                                               });
                schedule.dayofmonth = Models.SchedulingDayDetails.newInstance({
                                                                                  day: "1/1"
                                                                              });
                break;

            case Models.ScheduleRecurrence.Weekly:
                schedule.weekday = Models.SchedulingDayDetails.newInstance({
                                                                               day: this.getValuesFromLookup(model.selectedDays)
                                                                           });
                break;

            case Models.ScheduleRecurrence.Monthly:
                if (model.excludeMonth)
                {
                    schedule.month = this.getValuesFromLookup(model.selectedMonths);
                }
                if (model.monthlyOption == "week")
                {
                    schedule.weekday = Models.SchedulingDayDetails.newInstance({
                                                                                   day: this.getValuesFromLookup(model.monthlySelectedWeeks)
                                                                               });
                    if (model.weeklyEvery != "*")
                    {
                        schedule.weekday.day = schedule.weekday.day + "#" + model.weeklyEvery;
                    }
                }
                if (model.monthlyOption == "day")
                {
                    schedule.dayofmonth = Models.SchedulingDayDetails.newInstance({
                                                                                      day: this.getValuesFromLookup(model.monthlySelectedDays)
                                                                                  });
                }
                break;

        }
        return schedule;
    }

    private getValuesFromLookup(lookup: Lookup<boolean>): string
    {
        const values = [];

        for (const id in lookup)
        {
            if (lookup[id])
            {
                values.push(id);
            }
        }

        return values.join(",");
    }

    public static noEndDate(): Date
    {
        return new Date(2036, 11, 31, 23, 59, 59);
    }

    public toScheduleObject(model: Models.ScheduleDetails): ScheduleFormData
    {
        let scheduleFormData        = new ScheduleFormData();
        scheduleFormData.recurrence = model.recurrence;

        let time = model.schedule.time;
        scheduleFormData.start.setHours(this.parseTimeField(time.hour));
        scheduleFormData.start.setMinutes(this.parseTimeField(time.minute));
        scheduleFormData.start.setSeconds(this.parseTimeField(time.seconds));

        const endDate        = MomentHelper.parseOrNull(model.end_run);
        scheduleFormData.end = endDate ? endDate.toDate() : ScheduleService.noEndDate();

        switch (model.recurrence)
        {
            case Models.ScheduleRecurrence.Minutes:
                scheduleFormData.repeatEvery = this.parseInterval(time.minute);
                break;

            case Models.ScheduleRecurrence.Hours:
                scheduleFormData.repeatEvery = this.parseInterval(time.hour);
                break;

            case Models.ScheduleRecurrence.Once:
                scheduleFormData.start.setFullYear(parseInt(model.schedule.year));
                scheduleFormData.start.setMonth(parseInt(model.schedule.month) - 1);
                scheduleFormData.start.setDate(parseInt(model.schedule.dayofmonth.day));
                break;

            case Models.ScheduleRecurrence.Weekly:
                for (let weekDay of model.schedule.weekday.day.split(","))
                {
                    scheduleFormData.selectedDays[weekDay] = true;
                }
                break;

            case Models.ScheduleRecurrence.Monthly:
                if (model.schedule.month != "*")
                {
                    scheduleFormData.excludeMonth = true;
                    for (let month of model.schedule.month.split(","))
                    {
                        scheduleFormData.selectedMonths[month] = true;
                    }
                }

                if (model.schedule.weekday)
                {
                    scheduleFormData.monthlyOption = "week";
                    let days                       = model.schedule.weekday.day;
                    scheduleFormData.weeklyEvery   = "*";
                    if (model.schedule.weekday.day.indexOf("#") >= 0)
                    {
                        const daysSplit              = model.schedule.weekday.day.split("#");
                        days                         = daysSplit[0];
                        scheduleFormData.weeklyEvery = daysSplit[1];
                    }
                    for (let weekDay of days.split(","))
                    {
                        scheduleFormData.monthlySelectedWeeks[weekDay] = true;
                    }
                }
                else
                {
                    scheduleFormData.monthlyOption = "day";

                    for (let day of model.schedule.dayofmonth.day.split(","))
                    {
                        scheduleFormData.monthlySelectedDays[day] = true;
                    }
                }
                break;
        }
        return scheduleFormData;
    }

    private repeatEveryValue(model: ScheduleFormData): number
    {
        return model.repeatEvery > 0 ? model.repeatEvery : 1;
    }

    private parseTimeField(value: string): number
    {
        const parsed = parseInt(value?.split("/")[0]);
        return isNaN(parsed) ? 0 : parsed;
    }

    private parseInterval(value: string): number
    {
        const parsed = parseInt(value?.split("/")[1]);
        return isNaN(parsed) || parsed < 1 ? 1 : parsed;
    }

    public weeklyEveryOptions(): ControlOption<string>[]
    {
        return [
            new ControlOption("*", "Every"),
            new ControlOption("1", "The First"),
            new ControlOption("2", "The Second"),
            new ControlOption("3", "The Third"),
            new ControlOption("4", "The Fourth"),
            new ControlOption("5", "The Fifth")
        ];
    }
}

export class ScheduleFormData
{
    start: Date                           = new Date();
    end: Date                             = new Date();
    recurrence: Models.ScheduleRecurrence = null;
    repeatEvery: number                   = 1;
    weekdays: string                      = null;
    monthlyOption: string                 = null;
    weeklyEvery: string                   = null;
    selectedDays: Lookup<boolean>         = {};
    monthlySelectedDays: Lookup<boolean>  = {};
    monthlySelectedWeeks: Lookup<boolean> = {};
    selectedMonths: Lookup<boolean>       = {};
    excludeMonth: boolean;
}
